import React, { useEffect, useState } from "react";
import { FaRegUser } from "react-icons/fa6";
import { FiHash } from "react-icons/fi";
import { IoCallOutline } from "react-icons/io5";
import { GoOrganization } from "react-icons/go";
import { MdSettings } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Switch,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
} from "@mui/material";
import toast from "react-hot-toast";
import apiClient from "../../services/apiClient";

import PageLoader from "../../components/Loading/PageLoader";
import CustomButton from "../../components/Button/CustomButton";

const Settings = () => {
  const [userProfile, setUserProfile] = useState();
  const [loading, setLoading] = useState({
    fetchDetails: false,
    submitProfileDetail: false,
    submitSettingsDetails: false,
  });
  const [profileData, setProfileData] = useState({
    name: "",
    phone_number: "",
    email_id: "",
  });

  const userInfo = useSelector((state) => state?.userInfo?.user);

  const [otherSettings, setOtherSettings] = useState(userInfo?.settings);

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    setLoading({ ...loading, fetchDetails: true });

    try {
      const response = await apiClient.get(`/user/${userInfo?.id}`);

      setUserProfile(response?.data);
      setProfileData({
        name: response?.data?.user?.name,
        phone_number: response?.data?.user?.phone_number,
        email_id: response?.data?.user?.email_id,
      });
      setOtherSettings(response?.data?.user?.settings);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading({ ...loading, fetchDetails: false });
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleOtherSettingsChange = (e) => {
    const { name, value } = e.target;
    setOtherSettings((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, submitProfileDetail: true });

    try {
      const payload = new FormData();
      payload.append("name", profileData?.name);
      payload.append("phone_number", profileData?.phone_number);
      payload.append("email_id", profileData?.email_id);
      payload.append("user_id", userInfo?.id);

      await apiClient.put("/user/update", payload);

      fetchDetails();
      toast.success("Updated !");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading({ ...loading, submitProfileDetail: false });
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, submitSettingsDetails: true });

    try {
      const payload = new FormData();
      payload.append("user_id", userInfo?.id);
      payload.append("settings", JSON.stringify(otherSettings));

      await apiClient.put("/user/update", payload);

      fetchDetails();
      toast.success("Setting Changed !");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading({ ...loading, submitSettingsDetails: false });
    }
  };

  return (
    <>
      {loading?.fetchDetails && <PageLoader text="loading profile" />}



      <div className="pageTitle1 px-5 py-4">
        <h3>Settings</h3>
        <p>Manage your general information</p>
      </div>

    <div className="container">

      <Box sx={{ p: 3 }}>

        {/* Profile Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box sx={{ px: 5, py: 3, }}>
            {/* Profile Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: "#667eea",
                  fontSize: "2rem",
                  fontWeight: 600,
                }}
              >
                {userProfile?.user?.name?.charAt(0) || "U"}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {userProfile?.user?.name || "Unknown"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userProfile?.user?.email_id || "@.com"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, opacity: 1, }} />

            {/* Profile Form */}
            <form className="row" onSubmit={handleProfileSubmit}>
              <div className="col-12">
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Profile
                </Typography>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <FaRegUser />
                  </span>
                  <input
                    type="text"
                    name="name"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={profileData?.name}
                    required
                  />
                  <label htmlFor="name">Name</label>
                </div>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <IoCallOutline />
                  </span>
                  <input
                    type="text"
                    name="phone_number"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={profileData?.phone_number}
                    required
                  />
                  <label htmlFor="phone_number">Personal Phone Number</label>
                </div>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <FiHash />
                  </span>
                  <input
                    type="email"
                    name="email_id"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={profileData?.email_id}
                  />
                  <label htmlFor="number">Email</label>
                </div>
              </div>

              <div className="col-12 mt-4">
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Company Details
                </Typography>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <GoOrganization />
                  </span>
                  <input
                    type="text"
                    name="company_name"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={userProfile?.company?.company_name}
                    required
                  />
                  <label htmlFor="company_name">Company Name</label>
                </div>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <GoOrganization />
                  </span>
                  <input
                    type="text"
                    name="domain"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={userProfile?.company?.domain}
                    required
                  />
                  <label htmlFor="domain">Company Domain</label>
                </div>
              </div>

              <div className="col-xl-6">
                <div className="inputData withIcon my-3">
                  <span className="icon">
                    <GoOrganization />
                  </span>
                  <input
                    type="text"
                    name="company_owner_name"
                    className="form-control my-2"
                    onChange={handleProfileChange}
                    value={userProfile?.company?.company_owner_name}
                    required
                  />
                  <label htmlFor="company_owner_name">Company Admin</label>
                </div>
              </div>

              <div className="col-12">
                <div className="d-flex justify-content-end gap-3 mb-4 mt-3">
                  <CustomButton
                    type="button"
                    variant="contained"
                    sx={{ 
                      backgroundColor: "#FFFFFF",
                      color: "#000000",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      border: "1px solid #d9d9d9",
                      boxShadow: "none",
                      minWidth: "120px",
                      "&:hover": {
                        backgroundColor: "#f7f6ffff",
                        boxShadow: "none",
                      }
                    }}
                  >
                    Cancel
                  </CustomButton>

                  <CustomButton
                    type="submit"
                    variant="contained"
                    loading={loading?.submitProfileDetail}
                    sx={{ 
                      backgroundColor: "#6859f7ff",
                      color: "#FFFFFF",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      border: "1px solid #6859f7ff",
                      boxShadow: "none",
                      minWidth: "150px",
                      "&:hover": {
                        boxShadow: "none",
                      }
                    }}
                  >
                    Save
                  </CustomButton>
                </div>
              </div>

            </form>
          </Box>
        </Paper>

        {/* Extension Settings Section (Admin Only) */}
        {userInfo?.role === "3" && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Extension Settings
              </Typography>

              <form className="row" onSubmit={handleSettingsSubmit}>
                <div className="col-12">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Default
                  </Typography>
                </div>

                <div className="col-xl-5">
                  <div className="inputData withIcon my-3">
                    <span className="icon">
                      <FiHash />
                    </span>
                    <input
                      type="text"
                      name="caller_id"
                      className="form-control my-2"
                      onChange={handleOtherSettingsChange}
                      value={otherSettings?.default_extension?.caller_id}
                      required
                    />
                    <label htmlFor="caller_id">Allocate Number</label>
                  </div>
                </div>

                <div className="col-xl-5">
                  <div className="inputData withIcon my-3">
                    <span className="icon">
                      <FiHash />
                    </span>
                    <input
                      type="text"
                      name="extension"
                      className="form-control my-2"
                      onChange={handleOtherSettingsChange}
                      value={otherSettings?.default_extension?.extension}
                      required
                    />
                    <label htmlFor="extension">Extensions</label>
                  </div>
                </div>

                <div className="col-xl-5 my-2">
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend">Auto Connections</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={otherSettings?.auto_connect}
                            onChange={(e) =>
                              setOtherSettings({
                                ...otherSettings,
                                auto_connect: !otherSettings?.auto_connect,
                              })
                            }
                            name="auto_connect"
                          />
                        }
                        label={otherSettings?.auto_connect ? "Auto" : "Manual"}
                      />
                    </FormGroup>
                    <FormHelperText>Connects the extension automatically</FormHelperText>
                  </FormControl>
                </div>

                <div className="col-12 my-4">
                  <Divider />
                </div>

                <div className="col-12">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Choose Default AI Agent
                  </Typography>
                </div>

                <div className="col-xl-5">
                  <div className="inputData selectForm my-3">
                    <label>Model</label>
                    <span className="icon">
                      <FiHash />
                    </span>
                    <select className="form-select">
                      <option>Lead Horizon</option>
                      <option value="1">One</option>
                      <option value="2">Two</option>
                      <option value="3">Three</option>
                    </select>
                  </div>
                </div>

                <div className="col-12">
                  <div className="d-flex justify-content-end gap-3 mb-4 mt-3">
                    <CustomButton
                      type="button"
                      variant="contained"
                      color="secondary"
                      size="large"
                      width="20%"
                    >
                      Cancel
                    </CustomButton>

                    <CustomButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={loading?.submitSettingsDetails}
                      size="large"
                      width="20%"
                    >
                      Save Changes
                    </CustomButton>
                  </div>
                </div>
              </form>
            </Box>
          </Paper>
        )}
      </Box>

    </div>

    
    
    </>
  );
};

export default Settings;
